import React, { useEffect, useRef, useState } from 'react';
import { ExclamationTriangleIcon, PhotoIcon, TrashIcon } from '@heroicons/react/24/outline';

/**
 * La imagen de los correos de esta empresa: logo, color, firma y pie legal.
 *
 * **ClaimUp no aparece en el correo.** No es una preferencia estética: el deudor
 * tiene que reconocer a quien le factura, y un intermediario en el pie le da una
 * excusa para dudar de si la deuda es suya. Por eso esta tarjeta no ofrece
 * ninguna opción de «mostrar ClaimUp»: no existe.
 *
 * **La vista previa la compone el backend**, con la misma función que manda los
 * correos de verdad. Es deliberado y cuesta una llamada: una maqueta parecida
 * escrita aquí divergiría en la primera corrección que se le haga a una de las
 * dos, y entonces lo que se aprueba en pantalla no sería lo que recibe el deudor.
 */

const TOPE_KB = 500;

const TEXTO_DE_PRUEBA =
  'Hola Jessica,\n\n' +
  'Le escribimos por la factura **F-2026-0451**, de 300,00 €, que nos consta pendiente.\n\n' +
  'Si ya la ha abonado o hay algo que revisar, responda a este correo y lo vemos. ' +
  'Si prefiere resolverlo ahora, puede hacerlo aquí: https://pago.ejemplo.com/F-2026-0451';

/**
 * Un campo con su etiqueta y su ayuda.
 *
 * Con `id`, la etiqueta es un `<label for>` de verdad y el control queda con
 * nombre accesible —clicar el texto lleva al campo, y un lector de pantalla lo
 * anuncia—. Sin `id` (el bloque del logo, que son varios botones y no un control
 * solo) es texto corriente: un `<label>` suelto, sin nada asociado, promete algo
 * que no cumple.
 */
const Campo = ({ etiqueta, ayuda, id, children }) => (
  <div>
    {id ? (
      <label htmlFor={id} className='block text-xs font-medium text-slate-600 mb-1'>
        {etiqueta}
      </label>
    ) : (
      <p className='block text-xs font-medium text-slate-600 mb-1'>{etiqueta}</p>
    )}
    {children}
    {ayuda ? <p className='text-[11px] text-slate-400 mt-1'>{ayuda}</p> : null}
  </div>
);

const BrandCard = ({ marca, onGuardar, onSubirLogo, onQuitarLogo, onPrevisualizar, nombreEmpresa }) => {
  const [borrador, setBorrador] = useState({
    primary_color: marca.primary_color,
    signature: marca.signature,
    legal_footer: marca.legal_footer,
  });
  const [guardando, setGuardando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState('');
  const [previa, setPrevia] = useState(null);
  const ficheroRef = useRef(null);

  const sucio =
    borrador.primary_color !== marca.primary_color ||
    borrador.signature !== marca.signature ||
    borrador.legal_footer !== marca.legal_footer;

  // La previa se refresca con lo GUARDADO, no con el borrador: la compone el
  // servidor a partir de la marca de la base, así que enseñarla antes de guardar
  // mostraría el color anterior y haría dudar de si se guardó.
  useEffect(() => {
    let vivo = true;
    onPrevisualizar({ body_text: TEXTO_DE_PRUEBA, company_name: nombreEmpresa })
      .then(res => {
        if (vivo) setPrevia(res);
      })
      .catch(() => {
        if (vivo) setPrevia(null);
      });
    return () => {
      vivo = false;
    };
  }, [onPrevisualizar, nombreEmpresa, marca]);

  const guardar = async () => {
    setGuardando(true);
    setError('');
    try {
      await onGuardar(borrador);
    } catch (err) {
      setError(err?.response?.data?.error?.message || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  const elegirLogo = async evento => {
    const file = evento.target.files?.[0];
    // El input se limpia siempre, para que elegir el mismo fichero otra vez
    // —después de recortarlo, por ejemplo— vuelva a disparar el cambio.
    evento.target.value = '';
    if (!file) return;

    setSubiendo(true);
    setError('');
    try {
      await onSubirLogo(file);
    } catch (err) {
      setError(err?.response?.data?.error?.message || 'No se pudo subir el logo');
    } finally {
      setSubiendo(false);
    }
  };

  const quitarLogo = async () => {
    setSubiendo(true);
    setError('');
    try {
      await onQuitarLogo();
    } catch (err) {
      setError(err?.response?.data?.error?.message || 'No se pudo quitar el logo');
    } finally {
      setSubiendo(false);
    }
  };

  return (
    <div className='bg-white border border-slate-200 rounded-lg p-4'>
      <div className='mb-4'>
        <h3 className='text-sm font-semibold text-slate-900'>Cómo se ve tu correo</h3>
        <p className='text-xs text-slate-500 mt-0.5'>
          El correo sale con tu logo y tu nombre. ClaimUp no aparece en ninguna parte.
        </p>
      </div>

      {!marca.logo_servable && marca.has_logo ? (
        <div className='mb-4 flex gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-2.5'>
          <ExclamationTriangleIcon className='w-4 h-4 shrink-0 mt-px' />
          <span>
            El logo está guardado pero <strong>ahora mismo no se puede servir</strong>, así que los correos
            salen sin él. Falta configurar la dirección pública del servidor (<code>PUBLIC_API_URL</code>).
            Avisa a ClaimUp.
          </span>
        </div>
      ) : null}

      {error ? (
        <div className='mb-4 text-xs bg-red-50 border border-red-200 text-red-700 rounded-lg p-2.5'>{error}</div>
      ) : null}

      <div className='grid gap-6 md:grid-cols-2'>
        <div className='space-y-4'>
          <Campo
            etiqueta='Logo'
            ayuda={`PNG, JPG, WEBP o GIF, hasta ${TOPE_KB} KB. Se ve arriba del correo, a 40 px de alto.`}
          >
            <div className='flex items-center gap-3'>
              {marca.logo_url ? (
                <img
                  src={marca.logo_url}
                  alt={nombreEmpresa || 'Tu logo'}
                  className='h-10 max-w-[160px] object-contain border border-slate-200 rounded-lg bg-white p-1'
                />
              ) : (
                <div className='h-10 w-10 flex items-center justify-center border border-dashed border-slate-300 rounded-lg text-slate-300'>
                  <PhotoIcon className='w-5 h-5' />
                </div>
              )}

              <button
                type='button'
                onClick={() => ficheroRef.current?.click()}
                disabled={subiendo}
                className='px-2.5 py-1.5 text-xs rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50'
              >
                {marca.has_logo ? 'Cambiar' : 'Subir logo'}
              </button>

              {marca.has_logo ? (
                <button
                  type='button'
                  onClick={quitarLogo}
                  disabled={subiendo}
                  aria-label='Quitar logo'
                  className='p-1.5 text-slate-400 hover:text-red-600 disabled:opacity-50'
                >
                  <TrashIcon className='w-4 h-4' />
                </button>
              ) : null}

              <input
                ref={ficheroRef}
                type='file'
                accept='image/png,image/jpeg,image/webp,image/gif'
                onChange={elegirLogo}
                className='sr-only'
                aria-label='Logo'
              />
            </div>
          </Campo>

          <Campo etiqueta='Color' ayuda='Se usa en los enlaces del correo, como el de pago.'>
            <div className='flex items-center gap-2'>
              <input
                type='color'
                value={borrador.primary_color}
                onChange={e => setBorrador(b => ({ ...b, primary_color: e.target.value }))}
                className='h-8 w-12 rounded-lg border border-slate-200 cursor-pointer'
                aria-label='Color'
              />
              <input
                type='text'
                value={borrador.primary_color}
                onChange={e => setBorrador(b => ({ ...b, primary_color: e.target.value }))}
                className='w-28 px-2 py-1.5 text-xs font-mono border border-slate-200 rounded-lg'
                aria-label='Color en hexadecimal'
              />
            </div>
          </Campo>

          <Campo
            id='marca-firma'
            etiqueta='Firma'
            ayuda='Quién firma el correo. Va debajo del texto, en todas las plantillas.'
          >
            <textarea
              id='marca-firma'
              rows={3}
              maxLength={400}
              value={borrador.signature}
              onChange={e => setBorrador(b => ({ ...b, signature: e.target.value }))}
              placeholder={'Marta Ruiz\nAdministración'}
              className='w-full px-2.5 py-2 text-xs border border-slate-200 rounded-lg'
            />
          </Campo>

          <Campo
            id='marca-pie'
            etiqueta='Pie legal'
            ayuda='Razón social, dirección, aviso de protección de datos. Sale abajo, en letra pequeña.'
          >
            <textarea
              id='marca-pie'
              rows={3}
              maxLength={1000}
              value={borrador.legal_footer}
              onChange={e => setBorrador(b => ({ ...b, legal_footer: e.target.value }))}
              className='w-full px-2.5 py-2 text-xs border border-slate-200 rounded-lg'
            />
          </Campo>

          <button
            type='button'
            onClick={guardar}
            disabled={!sucio || guardando}
            className='px-3 py-1.5 text-xs rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40'
          >
            {/* «Guardar diseño», no «Guardar»: la tarjeta de la cadencia, justo
                debajo, ya tiene un «Guardar», y dos botones con el mismo nombre en
                la misma pantalla no se distinguen ni leyendo ni con un lector. */}
            {guardando ? 'Guardando…' : 'Guardar diseño'}
          </button>
        </div>

        <div>
          <p className='text-xs font-medium text-slate-600 mb-1'>Vista previa</p>
          <p className='text-[11px] text-slate-400 mb-2'>
            Es el correo de verdad: lo monta el servidor con la misma plantilla que usa al enviar.
          </p>

          {previa?.html ? (
            // En un iframe y no con dangerouslySetInnerHTML: el HTML del correo
            // trae sus propios estilos y, sin aislarlo, se mezclarían con los de
            // la aplicación y la previa dejaría de parecerse al correo.
            <iframe
              title='Vista previa del correo'
              srcDoc={previa.html}
              sandbox=''
              className='w-full h-[460px] border border-slate-200 rounded-lg bg-white'
            />
          ) : (
            <div className='w-full h-[460px] border border-dashed border-slate-200 rounded-lg flex items-center justify-center text-xs text-slate-400'>
              Cargando la vista previa…
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrandCard;
